import type { Theme } from '@material-ui/core'
import { makeStyles, Typography, Box, capitalize } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import SaveIcon from '@material-ui/icons/Save'
import type { QuoteView } from '@paintscout/react-quote'
import { QuoteContext } from '@paintscout/react-quote'
import type { WithUseStyles } from '@paintscout/ui'
import {
  AlertDialog,
  StatusDot,
  QuoteTagChip,
  useUser,
  Button,
  useClientOptions,
  DialogStackContext,
  scaleVariant,
  useIsMobile
} from '@paintscout/ui'
import { getObjectLabels, getStatus, getStatusOptions, getTags } from '@paintscout/util/builder'
import EditQuoteInfoDialog from 'src/components/dialogs/EditQuoteInfoDialog'
import type { Pipeline, QuoteDocument, QuoteStatus } from 'paintscout'
import React, { useContext } from 'react'
import QuoteTemplateTitle from './QuoteTemplateTitle'
import SendButton from './SendButton'
import Moment from 'react-moment'
import find from 'lodash/find'

import { CopyQuoteDialog } from 'src/components/dialogs'
import classNames from 'classnames'

export interface EditableQuoteHeaderProps {
  classes?: WithUseStyles<typeof useStyles>['classes']
  pipeline?: Pipeline
  view: QuoteView
  handleChangeView: (view: QuoteView) => void
  isNew?: boolean
  sticky?: boolean
}

const useStyles = makeStyles<Theme, EditableQuoteHeaderProps>((theme) => ({
  root: {
    width: '100%',
    '@media print': {
      display: 'none'
    }
  },
  content: {
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      paddingRight: 0
    }
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start'
    }
  },
  quoteTitle: (props) => {
    return {
      lineHeight: theme.typography.pxToRem(44),
      marginBottom: theme.typography.pxToRem(4),
      textAlign: 'left',
      maxWidth: 720,
      [theme.breakpoints.down('md')]: {
        maxWidth: 470
      },
      [theme.breakpoints.down('sm')]: {
        maxWidth: 500
      },
      [theme.breakpoints.down('xs')]: {
        maxWidth: 200
      },
      ...(props.sticky ? scaleVariant(theme.typography.h1, 0.7) : {})
    }
  },

  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'start'
    }
  },
  statusSelect: {
    marginRight: 8,
    marginLeft: 8
  },
  errorChip: {
    background: theme.palette.error.main,
    color: theme.palette.error.contrastText
  },
  chips: {},
  sendButton: {
    marginLeft: theme.spacing(),
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(0),
      marginTop: theme.spacing(1)
    }
  },
  rightSide: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  lastSavedText: (props) => {
    return {
      textAlign: 'left',
      marginTop: theme.spacing(0.5),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center'
      },
      ...(props.sticky ? scaleVariant(theme.typography.subtitle1, 0.9) : theme.typography.subtitle1)
    }
  },
  tags: {
    paddingTop: 8,
    cursor: 'pointer',
    width: 'fit-content'
  }
}))

function EditableQuoteHeader(props: EditableQuoteHeaderProps) {
  const classes = useStyles(props)
  const { view, isNew, sticky, handleChangeView, pipeline } = props
  const { isCrew, isSuperadmin, isSupport } = useUser()
  const { quote, cleanQuote, save, isDirty, isSaving, hasActiveAccount } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const isMobile = useIsMobile({ xs: true })
  const calculationLock = quote?.calculationLock && (!isSuperadmin || !isSupport)
  const isTemplate = quote.type === 'quoteTemplate'

  const tags = quote.tags ? getTags({ options, keys: quote.tags }) : []

  const showTags = tags && !isTemplate && !sticky && view !== 'work-order'

  function getQuoteTitle() {
    const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
    if (quote.fromTemplate && typeof quote.fromTemplate !== 'string') {
      return quote.fromTemplate.label
    } else if (quote.int_ext?.label) {
      return `${quote.int_ext?.label} ${objectLabels.quote.value}`
    } else if (quote.int_ext?.value === 'ext') {
      return `Exterior ${objectLabels.quote.value}`
    } else if (quote.int_ext?.value === 'all') {
      return `Interior / Exterior ${objectLabels.quote.value}`
    } else {
      return `Interior ${objectLabels.quote.value}`
    }
  }

  function handleTitleClick(ev: React.MouseEvent) {
    ev.preventDefault()

    // @ts-ignore
    ev.currentTarget?.blur() // clear focus outline after click

    if (calculationLock) {
      const objectLabels = getObjectLabels({ options, quote })
      openDialog(AlertDialog, {
        title: `${objectLabels.quote.value} Locked`,
        message: `This ${objectLabels.quote.value.toLowerCase()} may not be compatible with the latest version of PaintScout. To edit it, copy to a new estimate and make changes there.`,
        onClose: dismissDialog,
        actions: [
          {
            label: 'Cancel',
            onClick: dismissDialog,
            left: true,
            variant: 'text'
          },
          {
            label: `Copy to New ${objectLabels.quote.value}`,
            onClick: () => {
              openDialog(CopyQuoteDialog, {
                quoteId: quote._id,
                calculate: true,
                isInvoice: quote.is_invoice
              })
            }
          }
        ]
      })
      return
    }

    openDialog(EditQuoteInfoDialog, {
      onConfirm: async () => {
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  return (
    <div className={classes.root} data-testid="quote-header">
      <Grid container className={classes.content} justifyContent="space-between" alignItems="center">
        <Grid item>
          {isTemplate && <QuoteTemplateTitle sticky={sticky} classes={{ quoteTitle: classes.quoteTitle }} />}
          {!isTemplate && (
            <button
              className={classes.title}
              style={hasActiveAccount && !calculationLock ? {} : { cursor: 'default' }}
              onClick={hasActiveAccount && !isCrew ? handleTitleClick : null}
            >
              <Typography variant="h1" component="h1" className={classes.quoteTitle} noWrap>
                {getQuoteTitle()}
              </Typography>
              <Subtitle
                pipeline={pipeline}
                quote={quote}
                sticky={sticky}
                lastSaved={
                  !isNew && cleanQuote.dates.updated && isMobile ? (
                    <Moment
                      date={cleanQuote.dates.updated}
                      fromNow={true}
                      element={({ children }) => <span>Last saved&nbsp;{children}</span>}
                    />
                  ) : null
                }
              />
            </button>
          )}
        </Grid>

        {/* save & send buttons */}
        <Grid item classes={{ root: classes.rightSide }}>
          <Box display="flex" justifyContent="flex-end">
            {/* show normal save button if a template, or not mobile */}
            {(isTemplate || !isMobile) && (
              <Button
                data-testid="save-button"
                className="tour-quote-save-button"
                icon={SaveIcon}
                // size={isVerySmall ? 'small' : 'medium'}
                size="medium"
                disabled={!isDirty && !isSaving}
                loading={isSaving}
                onClick={() => save()}
              >
                Save
              </Button>
            )}
            {!isTemplate && (
              <SendButton
                disabled={!hasActiveAccount || isSaving}
                isNew={isNew}
                // size={smallButtons ? 'small' : 'medium'}
                size="medium"
                className={classNames(classes.sendButton, 'tour-quote-send-button')}
                view={view}
                requireSave={isMobile}
                handleChangeView={handleChangeView}
              />
            )}
          </Box>
          {!isNew && cleanQuote.dates.updated && !isMobile && (
            <Typography variant="subtitle1" className={classes.lastSavedText}>
              <Moment
                date={cleanQuote.dates.updated}
                fromNow={true}
                element={({ children }) => <span>Last saved&nbsp;{children}</span>}
              />
            </Typography>
          )}
        </Grid>
        <Grid container>
          {showTags && (
            <Grid container spacing={1} className={classes.tags} onClick={hasActiveAccount ? handleTitleClick : null}>
              {tags.map((tag) => {
                return (
                  <Grid item key={tag.name}>
                    <QuoteTagChip tag={tag} />
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

const useSubtitleStyles = makeStyles<Theme, { status: QuoteStatus; sticky: boolean }>((theme) => ({
  root: (props) => {
    return {
      display: 'flex',
      flexWrap: 'nowrap',
      ...(props.sticky ? scaleVariant(theme.typography.subtitle1, 0.9) : theme.typography.subtitle1)
    }
  },
  status: {
    display: 'inline-block',
    alignItems: 'center',
    textTransform: 'capitalize',
    color: (props) => {
      return theme.palette.quote.status[props.status]
    }
  },
  subtitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.5)
    }
  },
  icon: {
    fontSize: '0.8rem'
  },
  dot: {
    marginRight: theme.spacing(1)
  }
}))

const Subtitle: React.FC<{ quote: QuoteDocument; sticky: boolean; lastSaved?: JSX.Element; pipeline?: Pipeline }> = (
  props
) => {
  const { quote, lastSaved, pipeline: _pipeline, ...rest } = props
  const statusValue = getStatus({ quote })
  const statusOptions = getStatusOptions({})
  let status = find(statusOptions, { value: statusValue })
  if (!status) {
    status = {
      value: statusValue,
      label: capitalize(statusValue)
    }
  }
  const classes = useSubtitleStyles({ status: statusValue, ...props })
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })

  // name of quote type if it's a template
  const quoteLabel =
    quote.fromTemplate && typeof quote.fromTemplate !== 'string' && quote.int_ext.label
      ? `· ${quote.int_ext.label} ${objectLabels.quote.value}`
      : ''

  return (
    <div>
      <div className={classes.subtitleContainer}>
        <Typography variant="subtitle1" className={classNames(classes.root, classes.subtitle)}>
          <span className={classes.status} {...rest}>
            <StatusDot classes={{ dot: classes.dot }} status={status.value as QuoteStatus} size={10}>
              <span>{status.label}&nbsp;</span>
            </StatusDot>
          </span>
          {quoteLabel && <Typography>&nbsp;{quoteLabel}</Typography>}
        </Typography>
        {/* <Typography variant="body1">{pipeline?.label}</Typography> */}
      </div>
      <Typography variant="subtitle1" className={classNames(classes.root, classes.subtitle)}>
        {lastSaved && !quoteLabel && <span>&nbsp;{lastSaved}</span>}
      </Typography>
    </div>
  )
}

export default EditableQuoteHeader
